import { z } from "zod";
import {
  BERTH_TYPES,
  BOW_DIRECTIONS,
  CONTRACT_STATUSES,
  MOORING_MODES,
  PAYMENT_METHODS,
  PORT_PART_TYPES,
  ROLES,
  VESSEL_TYPES,
  WAITING_STATUSES,
} from "../constants";
import { normalizeRegistration } from "../format";
import {
  berthGeometrySchema,
  connectionsSchema,
  emailSchema,
  idSchema,
  isoDate,
  isoDateTime,
  lineSchema,
  localeSchema,
  longText,
  metres,
  nonNegativeCents,
  optionalEmail,
  optionalOib,
  passwordSchema,
  pointSchema,
  polygonSchema,
  positiveMetres,
  shortText,
  authoritySlugSchema,
  portSlugSchema,
} from "./common";
import { authoritySettingsSchema, fiscalSettingsSchema, tariffDefinitionSchema, vatSettingsSchema } from "./settings";

const nullableShort = shortText.nullish().transform((v) => (v === "" ? null : (v ?? null)));
const nullableLong = longText.nullish().transform((v) => (v === "" ? null : (v ?? null)));

// ---------------------------------------------------------------------------
// Authority & users

export const authorityCreateSchema = z.object({
  name: shortText.min(2),
  slug: authoritySlugSchema,
  oib: optionalOib,
  address: nullableShort,
  email: optionalEmail,
  phone: nullableShort,
  defaultLocale: localeSchema.default("hr"),
  adminEmail: emailSchema,
  adminName: shortText.min(2),
});

export const authorityUpdateSchema = z
  .object({
    name: shortText.min(2),
    slug: authoritySlugSchema,
    oib: optionalOib,
    address: nullableShort,
    email: optionalEmail,
    phone: nullableShort,
    web: nullableShort,
    defaultLocale: localeSchema,
    vat: vatSettingsSchema,
    fiscal: fiscalSettingsSchema,
    invoiceFooter: nullableLong,
    privacyTextHr: nullableLong,
    privacyTextEn: nullableLong,
    retentionMonths: z.number().int().min(1).max(240),
    settings: authoritySettingsSchema,
  })
  .partial();

export const roleAssignmentSchema = z.object({
  role: z.enum(ROLES).exclude(["platform_admin"]),
  portId: idSchema.nullable().default(null),
});

export const userCreateSchema = z.object({
  email: emailSchema,
  name: shortText.min(2),
  locale: localeSchema.default("hr"),
  roles: z.array(roleAssignmentSchema).min(1).max(50),
  password: passwordSchema.optional(),
});
export const userUpdateSchema = z
  .object({
    name: shortText.min(2),
    email: emailSchema,
    locale: localeSchema,
    status: z.enum(["active", "disabled"]),
  })
  .partial();
export const userRolesSchema = z.object({ roles: z.array(roleAssignmentSchema).min(0).max(50) });
export const userPasswordSchema = z.object({ password: passwordSchema });

// ---------------------------------------------------------------------------
// Ports and plan

export const portCreateSchema = z.object({
  name: shortText.min(2),
  slug: portSlugSchema,
  premisesCode: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9]{1,20}$/),
  location: pointSchema.nullish(),
  vhfChannel: z.string().trim().max(10).nullish(),
  phone: nullableShort,
  email: optionalEmail,
  maxStayHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 365)
    .nullish(),
  subtitleHr: nullableShort,
  subtitleEn: nullableShort,
  notesHr: nullableLong,
  notesEn: nullableLong,
  rulesHr: nullableLong,
  rulesEn: nullableLong,
  paymentInfoHr: nullableShort,
  paymentInfoEn: nullableShort,
  published: z.boolean().default(false),
  sort: z.number().int().default(0),
});
export const portUpdateSchema = portCreateSchema.partial();

export const portPartCreateSchema = z.object({
  portId: idSchema,
  type: z.enum(PORT_PART_TYPES),
  name: shortText.min(1),
  basin: nullableShort,
  polygon: polygonSchema.nullish(),
  sort: z.number().int().default(0),
});
export const portPartUpdateSchema = portPartCreateSchema.omit({ portId: true }).partial();

export const quayCreateSchema = z.object({
  portPartId: idSchema,
  name: shortText.min(1),
  line: lineSchema.nullish(),
  lengthOverrideM: positiveMetres.nullish(),
  mooringMode: z.enum(MOORING_MODES).default("stern_to"),
  depthM: metres.max(50).nullish(),
  gapM: metres.max(5).nullish(),
  allowRafting: z.boolean().default(false),
  connections: connectionsSchema.default([]),
  waterSide: z.enum(["left", "right"]).default("right"),
  maxVesselLengthM: positiveMetres.nullish(),
  sort: z.number().int().default(0),
});
export const quayUpdateSchema = quayCreateSchema.omit({ portPartId: true }).partial().extend({ portPartId: idSchema.optional() });

export const berthCreateSchema = z.object({
  portPartId: idSchema,
  quayId: idSchema.nullish(),
  code: z.string().trim().min(1).max(20),
  maxLengthM: positiveMetres.nullish(),
  maxBeamM: positiveMetres.nullish(),
  maxDraftM: positiveMetres.nullish(),
  connections: connectionsSchema.default([]),
  type: z.enum(BERTH_TYPES).default("permanent"),
  geometry: berthGeometrySchema.nullish(),
  positionM: metres.nullish(),
});
export const berthUpdateSchema = berthCreateSchema.partial();
export const berthBulkCreateSchema = z.object({
  portPartId: idSchema,
  quayId: idSchema.nullish(),
  firstNumber: z.number().int().min(1).max(9999),
  count: z.number().int().min(1).max(500),
  type: z.enum(BERTH_TYPES).default("permanent"),
  maxLengthM: positiveMetres.nullish(),
  maxBeamM: positiveMetres.nullish(),
  maxDraftM: positiveMetres.nullish(),
  connections: connectionsSchema.default([]),
});

export const tariffCreateSchema = z.object({
  portId: idSchema,
  name: shortText.min(1),
  validFrom: isoDate,
  validTo: isoDate.nullish(),
  definition: tariffDefinitionSchema,
});
export const tariffUpdateSchema = tariffCreateSchema.omit({ portId: true }).partial();

// ---------------------------------------------------------------------------
// Registry

export const companySchema = z.object({
  name: shortText.min(1),
  oib: optionalOib,
  address: nullableShort,
  email: optionalEmail,
});
export type Company = z.infer<typeof companySchema>;

export const registrationSchema = z.string().transform(normalizeRegistration).pipe(z.string().min(1).max(30));

export const vesselCreateSchema = z.object({
  registration: registrationSchema,
  name: nullableShort,
  type: z.enum(VESSEL_TYPES).default("motor"),
  lengthM: positiveMetres,
  beamM: positiveMetres.nullish(),
  draftM: positiveMetres.nullish(),
  flag: z.string().trim().max(3).nullish(),
  ownerPersonId: idSchema.nullish(),
  ownerCompany: companySchema.nullish(),
});
export const vesselUpdateSchema = vesselCreateSchema.partial();

export const personCreateSchema = z.object({
  name: shortText.min(2),
  oib: optionalOib,
  email: optionalEmail,
  phone: nullableShort,
  address: nullableShort,
});
export const personUpdateSchema = personCreateSchema.partial();

// ---------------------------------------------------------------------------
// Operations

export const stayCreateSchema = z.object({
  id: idSchema.optional(),
  portId: idSchema,
  vesselId: idSchema.optional(),
  vessel: vesselCreateSchema.partial({ lengthM: true }).extend({ lengthM: positiveMetres }).optional(),
  quayId: idSchema.nullish(),
  berthId: idSchema.nullish(),
  positionM: metres.nullish(),
  mooringMode: z.enum(["stern_to", "side_to"]).nullish(),
  raftedOnStayId: idSchema.nullish(),
  bowDirection: z.enum(BOW_DIRECTIONS).nullish(),
  arrivedAt: isoDateTime.optional(),
  expectedDeparture: isoDateTime.nullish(),
  connections: connectionsSchema.default([]),
  notes: nullableLong,
  phone: nullableShort,
  photoKey: z.string().max(300).nullish(),
});
export const stayUpdateSchema = z
  .object({
    quayId: idSchema.nullable(),
    berthId: idSchema.nullable(),
    positionM: metres.nullable(),
    mooringMode: z.enum(["stern_to", "side_to"]).nullable(),
    raftedOnStayId: idSchema.nullable(),
    bowDirection: z.enum(BOW_DIRECTIONS).nullable(),
    expectedDeparture: isoDateTime.nullable(),
    connections: connectionsSchema,
    notes: nullableLong,
  })
  .partial();
export const stayDepartSchema = z.object({ departedAt: isoDateTime.optional() });
export const stayExtendSchema = z.object({ expectedDeparture: isoDateTime });
export const stayMoveSchema = z.object({
  quayId: idSchema.nullable(),
  berthId: idSchema.nullable(),
  positionM: metres.nullable(),
  mooringMode: z.enum(["stern_to", "side_to"]).nullish(),
  raftedOnStayId: idSchema.nullish(),
  bowDirection: z.enum(BOW_DIRECTIONS).nullish(),
  /** the worker accepted the warning: boats tied to this one lose their place */
  releaseRafted: z.boolean().optional(),
});

export const instalmentSchema = z.object({
  due: isoDate,
  amountCents: nonNegativeCents,
  paidCents: nonNegativeCents.default(0),
});
export type Instalment = z.infer<typeof instalmentSchema>;

export const contractCreateSchema = z
  .object({
    berthId: idSchema,
    vesselId: idSchema.nullish(),
    personId: idSchema,
    startsOn: isoDate,
    endsOn: isoDate,
    amountCents: nonNegativeCents,
    schedule: z.array(instalmentSchema).max(24).optional(),
    instalments: z.number().int().min(1).max(12).optional(),
    status: z.enum(CONTRACT_STATUSES).default("active"),
    notes: nullableLong,
  })
  .refine((c) => c.endsOn > c.startsOn, { message: "error.invalidDate", path: ["endsOn"] });
export const contractUpdateSchema = z
  .object({
    vesselId: idSchema.nullable(),
    personId: idSchema,
    startsOn: isoDate,
    endsOn: isoDate,
    amountCents: nonNegativeCents,
    schedule: z.array(instalmentSchema).max(24),
    status: z.enum(CONTRACT_STATUSES),
    notes: nullableLong,
  })
  .partial();
export const contractRenewSchema = z.object({
  startsOn: isoDate.optional(),
  endsOn: isoDate.optional(),
  amountCents: nonNegativeCents.optional(),
  instalments: z.number().int().min(1).max(12).optional(),
});
export const contractPaymentSchema = z.object({
  amountCents: z.number().int().positive(),
  method: z.enum(PAYMENT_METHODS),
  instalmentIndex: z.number().int().min(0).optional(),
});

export const absenceCreateSchema = z
  .object({
    id: idSchema.optional(),
    berthId: idSchema,
    freeFrom: isoDate,
    freeUntil: isoDate,
    availableForTransit: z.boolean().default(true),
    notes: nullableLong,
  })
  .refine((a) => a.freeUntil >= a.freeFrom, { message: "error.invalidDate", path: ["freeUntil"] });
export const absenceUpdateSchema = z
  .object({ freeFrom: isoDate, freeUntil: isoDate, availableForTransit: z.boolean(), notes: nullableLong })
  .partial();

export const waitingCreateSchema = z.object({
  portId: idSchema,
  personId: idSchema,
  vesselId: idSchema.nullish(),
  requestedLengthM: positiveMetres,
  requestedBeamM: positiveMetres.nullish(),
  appliedAt: isoDate,
  priorityNotes: nullableLong,
});
export const waitingUpdateSchema = z
  .object({
    vesselId: idSchema.nullable(),
    requestedLengthM: positiveMetres,
    requestedBeamM: positiveMetres.nullable(),
    appliedAt: isoDate,
    status: z.enum(WAITING_STATUSES),
    priorityNotes: nullableLong,
  })
  .partial();
export const waitingOfferSchema = z.object({
  berthId: idSchema,
  startsOn: isoDate,
  endsOn: isoDate,
  amountCents: nonNegativeCents,
  instalments: z.number().int().min(1).max(12).default(1),
});

// ---------------------------------------------------------------------------
// Invoices

export const buyerSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("anonymous") }),
  z.object({
    type: z.literal("person"),
    personId: idSchema.nullish(),
    name: shortText.min(1),
    oib: optionalOib,
    address: nullableShort,
    email: optionalEmail,
  }),
  z.object({
    type: z.literal("company"),
    name: shortText.min(1),
    oib: z.string().regex(/^\d{11}$/),
    address: nullableShort,
    email: optionalEmail,
    isPublicBody: z.boolean().default(false),
  }),
]);
export type Buyer = z.infer<typeof buyerSchema>;

export const manualLineSchema = z.object({
  description: shortText.min(1),
  quantity: z.number().positive().max(100000),
  unit: z.enum(["day", "kwh", "m3", "pcs"]).default("pcs"),
  unitPriceCents: z.number().int(),
});

export const invoiceCreateSchema = z.object({
  portId: idSchema,
  stayId: idSchema.nullish(),
  contractId: idSchema.nullish(),
  buyer: buyerSchema.default({ type: "anonymous" }),
  paymentMethod: z.enum(PAYMENT_METHODS),
  /** For stays: bill until this instant (default: expected departure or now). */
  billUntil: isoDateTime.optional(),
  connections: connectionsSchema.optional(),
  metered: z.record(z.string(), z.number().min(0)).optional(),
  lines: z.array(manualLineSchema).max(50).optional(),
  markPaid: z.boolean().default(true),
  locale: localeSchema.default("hr"),
});

export const paymentCreateSchema = z.object({
  amountCents: z.number().int().positive(),
  method: z.enum(PAYMENT_METHODS),
  receivedAt: isoDateTime.optional(),
});

export const exportQuerySchema = z.object({
  from: isoDate,
  to: isoDate,
  portId: idSchema.optional(),
});

export const auditQuerySchema = z.object({
  entity: z.string().max(40).optional(),
  entityId: idSchema.optional(),
  userId: idSchema.optional(),
  action: z.string().max(40).optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});

// ---------------------------------------------------------------------------
// Auth

export const loginSchema = z.object({ email: emailSchema, password: z.string().min(1).max(200) });
export const passwordForgotSchema = z.object({ email: emailSchema, locale: localeSchema.optional() });
export const passwordResetSchema = z.object({ token: z.string().min(20).max(200), password: passwordSchema });
export const switchAuthoritySchema = z.object({ authorityId: idSchema });
